// Kopf der Ticket-Detailseite (design_handoff_tickets §3): Status-App-Icon, Infozeile, Titel (einzeilig),
// Chips, Werkzeugleiste (App-Icons) und unten rechts „Erledigt“ + Hauptaktion je Status.
import type { CSSProperties } from 'react';
import {
  ArrowBendDownLeft, ArrowCounterClockwise, Buildings, Check, CheckCircle, FilePdf, FileText, Flag, HandGrabbing, LockSimple,
  PencilSimple, Play, SpinnerGap, Trash, User, WarningCircle, Wrench, type Icon,
} from '@phosphor-icons/react';
import { AppIconKnopf, AppIconLeiste } from '../../../../components/ui/AppIconKnopf';
import { faelligListe, hauptAktion, zeigeErledigt, type HauptAktionArt } from '../../../../lib/tickets/darstellung';
import { STATUS_ICON, STATUS_ICON_STANDARD, prioFarbe, prioLabel, statusFarbe, statusLabel } from '../liste/teile';

const HAUPT_ICON: Record<HauptAktionArt, Icon> = {
  uebernehmen: HandGrabbing,
  starten: Play,
  beauftragen: Wrench,
  rueckmeldung: ArrowBendDownLeft,
  schliessen: LockSimple,
  oeffnen: ArrowCounterClockwise,
};

export interface DetailKopfTicket {
  ticket_nummer: string;
  titel: string;
  status: string;
  prioritaet: string;
  eskaliert: boolean;
  geloescht_am?: string | null;
  faellig_am: string | null;
  updated_at: string;
  zustaendig_id: string | null;
  abschluss_grund?: string | null;
  objekt?: { id: string; bezeichnung: string } | null;
  mieter_rel?: { id: string; vorname: string; nachname: string } | null;
}

export interface DetailKopfProps {
  ticket: DetailKopfTicket;
  heute: string;
  nurIcons: boolean;
  /** Touch/Mobil: keine Tooltips → Titel darf zweizeilig umbrechen */
  mobil?: boolean;
  bearbeiten: boolean;
  titelEntwurf: string;
  onTitelEntwurf: (t: string) => void;
  speichertGerade: boolean;
  beauftragenLaedt: boolean;
  aktionLaeuft: boolean;
  onBearbeiten: () => void;
  onAbbrechen: () => void;
  onFertig: () => void;
  onAngebot: () => void;
  onBeauftragen: () => void;
  onPdf: () => void;
  onPapierkorb: () => void;
  onZurueckholen: () => void;
  onErledigt: () => void;
  onHauptaktion: (art: HauptAktionArt, zielStatus: string | null) => void;
  onObjekt?: (id: string) => void;
  onMieter?: (id: string) => void;
}

function Chip({ farbe, icon: I, children, onClick, tip, flaeche }: { farbe: string; icon?: Icon; children: string; onClick?: () => void; tip?: string; flaeche?: string }) {
  const stil: CSSProperties = {
    height: 26, padding: '0 10px', gap: 6, borderRadius: 13, fontSize: 12, fontWeight: 800, border: 'none', fontFamily: 'inherit',
    background: flaeche ?? `color-mix(in srgb, ${farbe} 14%, transparent)`, color: farbe, maxWidth: '100%',
  };
  const inhalt = <>{I && <I size={13} weight="fill" aria-hidden />}<span className="truncate">{children}</span></>;
  return onClick
    ? <button type="button" onClick={onClick} data-tip={tip} className="immo-chip-knopf inline-flex min-w-0 shrink items-center" style={{ ...stil, cursor: 'pointer' }}>{inhalt}</button>
    : <span className="inline-flex min-w-0 shrink items-center" style={stil}>{inhalt}</span>;
}

export function DetailKopf(p: DetailKopfProps) {
  const t = p.ticket;
  const farbe = statusFarbe(t.status);
  const StatusIcon = STATUS_ICON[t.status] ?? STATUS_ICON_STANDARD;
  const papierkorb = !!t.geloescht_am;
  const abgeschlossen = t.status === 'erledigt' || t.status === 'geschlossen';
  const haupt = hauptAktion({ status: t.status, hatZustaendig: !!t.zustaendig_id, imPapierkorb: papierkorb, bearbeiten: p.bearbeiten });
  const HauptIcon = haupt ? HAUPT_ICON[haupt.art] : null;
  const erledigt = zeigeErledigt(t.status, papierkorb, p.bearbeiten);
  const f = faelligListe(t.faellig_am, t.status, p.heute);
  const aktualisiert = new Date(t.updated_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const mieterName = t.mieter_rel ? `${t.mieter_rel.vorname ?? ''} ${t.mieter_rel.nachname ?? ''}`.trim() : '';

  return (
    <section aria-label="Ticket" className="immo-glas relative flex shrink-0 flex-col" style={{
      borderRadius: 26, padding: 16, gap: 12,
      backgroundImage: `radial-gradient(120% 140% at 0% 0%, color-mix(in srgb, ${farbe} 16%, transparent), transparent 60%), var(--glass)`,
    }}>
      <div className="flex flex-wrap items-start" style={{ gap: 14 }}>
        <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{
          width: 42, height: 42, borderRadius: 13, background: farbe, color: '#fff', boxShadow: `0 10px 24px -10px ${farbe}`,
        }}>
          <StatusIcon size={22} weight="fill" />
        </span>
        <div className="flex min-w-0 flex-col" style={{ gap: 4, flex: '1 1 260px' }}>
          <span className="flex flex-wrap items-center" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>
            <span style={{ color: 'var(--ink2)', fontWeight: 800 }}>{t.ticket_nummer}</span>
            <span aria-hidden>·</span>
            <span style={{ color: farbe, fontWeight: 800 }}>{statusLabel(t.status)}</span>
            {f.ton !== 'leer' && <><span aria-hidden>·</span><span style={{ color: f.ton === 'ueberfaellig' ? 'var(--bad)' : undefined }}>Fällig: {f.text}</span></>}
            <span aria-hidden>·</span>
            <span>aktualisiert {aktualisiert}</span>
          </span>
          {p.bearbeiten ? (
            <input value={p.titelEntwurf} onChange={e => p.onTitelEntwurf(e.target.value)} aria-label="Titel" autoFocus
              data-tip="Titel – Kurze, eindeutige Beschreibung des Problems"
              style={{ height: 44, borderRadius: 13, padding: '0 12px', border: 'none', outline: 'none', background: 'var(--bg)',
                boxShadow: 'inset 0 0 0 1.5px var(--acc)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', minWidth: 0 }} />
          ) : (
            <h1 className={`m-0 ${p.mobil ? '' : 'truncate'}`} data-tip={p.mobil ? undefined : t.titel}
              style={{ fontSize: p.mobil ? 20 : 22, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.2,
                ...(p.mobil ? { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}) }}>{t.titel}</h1>
          )}
          <div className="flex flex-wrap items-center" style={{ gap: 6, marginTop: 2 }}>
            <Chip farbe={prioFarbe(t.prioritaet)} icon={Flag}>{prioLabel(t.prioritaet)}</Chip>
            {t.eskaliert && t.status !== 'eskaliert' && <Chip farbe="#a24bc9" icon={WarningCircle}>Eskaliert</Chip>}
            {papierkorb && <Chip farbe="var(--ink2)" flaeche="var(--fill2)" icon={Trash}>Papierkorb</Chip>}
            {t.objekt && <Chip farbe="var(--ink)" flaeche="var(--fill2)" icon={Buildings} onClick={p.onObjekt ? () => p.onObjekt?.(t.objekt!.id) : undefined} tip="Zum Objekt">{t.objekt.bezeichnung}</Chip>}
            {t.mieter_rel && <Chip farbe="var(--info)" flaeche="var(--info-soft)" icon={User} onClick={p.onMieter ? () => p.onMieter?.(t.mieter_rel!.id) : undefined} tip="Zum Mieter">{mieterName || 'Mieter'}</Chip>}
          </div>
        </div>
        {p.bearbeiten ? (
          <div className="flex shrink-0 items-center" style={{ gap: 4, padding: 4, borderRadius: 22, background: 'var(--fill2)' }}>
            <button type="button" onClick={p.onAbbrechen} disabled={p.speichertGerade} data-tip="Abbrechen – Änderungen verwerfen und zur Ansicht zurück"
              style={{ height: 40, padding: '0 16px', borderRadius: 18, border: 'none', background: 'transparent', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Abbrechen
            </button>
            <button type="button" onClick={p.onFertig} disabled={p.speichertGerade} data-tip="Speichern – Titel, Beschreibung und Felder übernehmen"
              className="inline-flex items-center"
              style={{ height: 40, padding: '0 16px', gap: 6, borderRadius: 18, border: 'none', background: 'var(--ok)', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, cursor: 'pointer', opacity: p.speichertGerade ? 0.6 : 1 }}>
              {p.speichertGerade ? <SpinnerGap size={15} className="animate-spin" /> : <Check size={15} weight="bold" />} Fertig
            </button>
          </div>
        ) : (
          <AppIconLeiste style={{ flexShrink: 0 }}>
            <AppIconKnopf icon={PencilSimple} farbe="orange" label="Bearbeiten" nurIcon={p.nurIcons} onClick={p.onBearbeiten}
              tip="Bearbeiten – Titel, Beschreibung, Kategorie, Objekt und Mieter ändern" />
            <AppIconKnopf icon={FileText} farbe="blau" label="Angebot" nurIcon={p.nurIcons} onClick={p.onAngebot} gesperrt={p.beauftragenLaedt}
              tip="Angebot anfragen – Kostenvoranschlag bei einem Dienstleister anfordern" />
            {haupt?.art !== 'beauftragen' && <AppIconKnopf icon={Wrench} farbe="gold" label="Beauftragen" nurIcon={p.nurIcons} onClick={p.onBeauftragen} gesperrt={p.beauftragenLaedt}
              tip="Handwerker beauftragen – Auftrag an einen Dienstleister vorbereiten und per Mail senden" />}
            <AppIconKnopf icon={FilePdf} farbe="gruen" label="PDF" nurIcon={p.nurIcons} onClick={p.onPdf}
              tip="PDF – Ticket mit Verlauf als PDF herunterladen (Zeitnachweis als zweite Datei)" />
            {papierkorb ? (
              <AppIconKnopf icon={ArrowCounterClockwise} farbe="grau" label="Zurückholen" nurIcon={p.nurIcons} onClick={p.onZurueckholen} gesperrt={p.aktionLaeuft}
                tip="Wiederherstellen – Ticket aus dem Papierkorb zurückholen" />
            ) : abgeschlossen && (
              <>
                <span aria-hidden style={{ width: 1, height: 30, background: 'var(--line)', margin: '0 2px' }} />
                <AppIconKnopf icon={Trash} farbe="rot" label="Löschen" nurIcon={p.nurIcons} onClick={p.onPapierkorb}
                  tip="In den Papierkorb – Abgeschlossenes Ticket ausblenden, jederzeit wiederherstellbar" />
              </>
            )}
          </AppIconLeiste>
        )}
      </div>

      {t.abschluss_grund && (
        <div style={{ padding: '10px 12px', borderRadius: 14, background: 'var(--ok-soft)' }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ok)' }}>Abschlussgrund</div>
          <div className="whitespace-pre-wrap" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginTop: 2 }}>{t.abschluss_grund}</div>
        </div>
      )}

      {(erledigt || haupt) && (
        <div className="flex flex-wrap items-center justify-end" style={{ gap: 8 }}>
          {erledigt && (
            <button type="button" onClick={p.onErledigt} disabled={p.aktionLaeuft} data-tip="Als erledigt markieren (mit Abschlussgrund)"
              className="inline-flex items-center"
              style={{ height: 36, padding: '0 14px', gap: 7, borderRadius: 18, border: 'none', background: 'var(--ok-soft)', color: 'var(--ok)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' }}>
              <CheckCircle size={16} weight="bold" /> Erledigt
            </button>
          )}
          {haupt && HauptIcon && (
            <button type="button" onClick={() => p.onHauptaktion(haupt.art, haupt.zielStatus)} disabled={p.aktionLaeuft || (haupt.art === 'beauftragen' && p.beauftragenLaedt)}
              data-tip={haupt.tip} className="inline-flex items-center"
              style={{ height: 36, padding: '0 16px', gap: 7, borderRadius: 18, border: 'none', background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)',
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800, cursor: 'pointer', opacity: p.aktionLaeuft ? 0.6 : 1 }}>
              {p.aktionLaeuft || (haupt.art === 'beauftragen' && p.beauftragenLaedt) ? <SpinnerGap size={16} className="animate-spin" /> : <HauptIcon size={16} weight="bold" />} {haupt.label}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
